import type { CostEstimate } from '@cartwheel/api/v1'
import { useState } from 'react'
import { Modal } from '../modal'
import { Tabs } from '../tabs'
import { CommercialEstimateForm } from './commercial-estimate-form'
import { MedicaidEstimateForm } from './medicaid-estimate-form'
import { OtherEstimateForm } from './other-estimate-form'
import { SelfPaidEstimateForm } from './self-paid-estimate-form'
import type { CostEstimateEditorMode, CostEstimateInteraction, EstimateType } from './types'

interface CostEstimateModalProps {
  clientId: string
  estimate: CostEstimate
  mode: CostEstimateEditorMode
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  onInteraction?: (interaction: CostEstimateInteraction) => void
}

const TABS: Array<{ value: EstimateType; label: string }> = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'self_pay', label: 'Self-Paid' },
  { value: 'other', label: 'Other' },
]

export function CostEstimateModal({
  clientId,
  estimate,
  mode,
  isOpen,
  onClose,
  onSaved,
  onInteraction,
}: CostEstimateModalProps) {
  const [activeType, setActiveType] = useState<EstimateType>(
    mode === 'edit' ? estimate.estimate_type : 'commercial'
  )
  // Pre-populate field values only when the active tab matches the saved
  // estimate's type; switching tabs gives a blank form for that type. The
  // `estimate` itself (including self_pay_message) is always passed through.
  const estimateForTab = mode === 'edit' && estimate.estimate_type === activeType ? estimate : null
  const activeMode = estimateForTab ? 'edit' : 'create'

  const handleTypeChange = (estimateType: EstimateType) => {
    setActiveType(estimateType)
    onInteraction?.({ type: 'type_selected', estimateType })
  }

  const handleCancel = () => {
    onInteraction?.({ type: 'editor_canceled', mode: activeMode, estimateType: activeType })
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={mode === 'edit' ? 'Edit Cost Estimate' : 'Create Cost Estimate'}
      subtitle={
        mode === 'edit'
          ? 'Any changes made here will update your current cost estimate draft.'
          : 'Create a new cost estimate for this client.'
      }
    >
      <div
        onSubmitCapture={() =>
          onInteraction?.({ type: 'save_submitted', mode: activeMode, estimateType: activeType })
        }
      >
        <Tabs value={activeType} onChange={handleTypeChange}>
          <Tabs.List ariaLabel="Insurance Type" className="mb-6">
            {TABS.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Tabs.Panel value="commercial">
            <CommercialEstimateForm
              clientId={clientId}
              estimate={estimateForTab}
              onSaved={onSaved}
              onCancel={handleCancel}
            />
          </Tabs.Panel>
          <Tabs.Panel value="medicaid">
            <MedicaidEstimateForm
              clientId={clientId}
              estimate={estimateForTab}
              onSaved={onSaved}
              onCancel={handleCancel}
            />
          </Tabs.Panel>
          <Tabs.Panel value="self_pay">
            <SelfPaidEstimateForm
              clientId={clientId}
              estimate={estimateForTab}
              previewMessage={estimate.self_pay_message}
              onSaved={onSaved}
              onCancel={handleCancel}
            />
          </Tabs.Panel>
          <Tabs.Panel value="other">
            <OtherEstimateForm
              clientId={clientId}
              estimate={estimateForTab}
              onSaved={onSaved}
              onCancel={handleCancel}
            />
          </Tabs.Panel>
        </Tabs>

        <div className="mt-4 text-sm text-charcoal-50 text-center">
          This will not be sent to the family yet.
        </div>
      </div>
    </Modal>
  )
}
